import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Typography,
  CircularProgress,
  IconButton,
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button } from "@mui/material";

const COMPLETED_GREEN = "#69AF45";
const DEFAULT_BLUE = "#0F5385";

const TeamCard = ({ group, loading = false, onClick, onEdit, onDelete }) => {
  const cardColor = group.completed ? COMPLETED_GREEN : DEFAULT_BLUE;
  const handleEditClick = (e) => {
    e.stopPropagation();
    if (onEdit) onEdit();
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (onDelete) onDelete();
  };

  return (
    <Grid size={4} sx={{ cursor: "pointer" }}>
      <Card
        onClick={loading ? () => {} : onClick}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          "&:hover": {
            boxShadow: group.completed
              ? "0 8px 24px rgba(105, 175, 69, 0.3)"
              : "0 8px 24px rgba(15, 83, 133, 0.2)",
            transform: "translateY(-4px)",
          },
          border: `1px solid ${cardColor}`,
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {/* Header with gradient background */}
        <Box
          sx={{
            background: group.completed
              ? `linear-gradient(135deg, ${COMPLETED_GREEN} 0%, #519342 100%)`
              : "linear-gradient(135deg, #0F5385 0%, #105B93 100%)",
            padding: "20px",
            color: "white",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <GroupsIcon sx={{ fontSize: 32, marginRight: 1, opacity: 0.8 }} />
            <Box display="flex" alignItems="center" gap={1}>
              {(onEdit || onDelete) && (
                <Box>
                  {onEdit && (
                    <IconButton
                      onClick={handleEditClick}
                      size="small"
                      sx={{
                        color: "white",
                        backgroundColor: "rgba(255,255,255,0.15)",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.25)",
                        },
                        mr: 0.5,
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  {onDelete && (
                    <IconButton
                      onClick={handleDeleteClick}
                      size="small"
                      sx={{
                        color: "white",
                        backgroundColor: "rgba(255,255,255,0.15)",
                        "&:hover": {
                          backgroundColor: "rgba(244,67,54,0.7)",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}
              <Chip
                label="Team"
                size="small"
                sx={{
                  backgroundColor: "rgba(255,255,255,0.25)",
                  color: "white",
                  fontWeight: 600,
                }}
              />
            </Box>
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              marginTop: "12px",
              wordBreak: "break-word",
            }}
          >
            {group.name}
          </Typography>
        </Box>

        {/* Content */}
        <CardContent
          sx={{
            flex: 1,
            padding: "20px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ minHeight: "120px" }}
            >
              <CircularProgress size={40} />
            </Box>
          ) : (
            <>
              {/* Members Info */}
              <Box sx={{ marginBottom: "20px" }}>
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{ marginBottom: "12px" }}
                >
                  <PersonIcon
                    sx={{ color: cardColor, marginRight: "8px", fontSize: 24 }}
                  />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "#666", fontWeight: 500, display: "block" }}
                    >
                      Team Members
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{ color: cardColor, fontWeight: 700 }}
                    >
                      {group.members?.length || 0}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    height: "1px",
                    backgroundColor: "#e0e0e0",
                    margin: "12px 0",
                  }}
                />
              </Box>

              {/* Team Status */}
              <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  padding: "12px",
                  borderRadius: "6px",
                  marginBottom: "16px",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Status
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: cardColor, fontWeight: 600, marginTop: "4px" }}
                >
                  {group.completed
                    ? "COMPLETE"
                    : group.members?.length > 0
                      ? "Active"
                      : "No Members"}
                </Typography>
              </Box>

              {/* View Details Button */}
              <Button
                fullWidth
                variant="contained"
                endIcon={<ArrowRightIcon />}
                sx={{
                  background: group.completed
                    ? `linear-gradient(135deg, ${COMPLETED_GREEN} 0%, #519342 100%)`
                    : "linear-gradient(135deg, #0F5385 0%, #105B93 100%)",
                  textTransform: "none",
                  fontWeight: 600,
                  padding: "10px",
                  color: "white",
                }}
              >
                View & Manage
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default TeamCard;
